import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { allFieldsAmount } from 'bl/fields.bl';
import nextTransferTimestamp from 'bl/payment/nextTransferTimestamp.bl';
import nextTransferWeekday from 'bl/payment/nextTransferWeekday.bl';
import splitPaymentsByOrigin from 'bl/payment/splitPaymentsByOrigin.bl';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SF from 'constants/salonFlag.const';
import WEEKDAY from 'constants/time/weekday.const';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonFlags } from 'selectors/currentSalon.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import sumArray from 'utils/arrays/sumArray.util';
import { formatDayToYearWeekday } from 'utils/date/format.util';
import nullBind from 'utils/functions/nullBind.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './PaymentArrival.module.scss';

const L = logger('PaymentArrival');

const getFlagPaymentArrival = nullBind(getAnyOrVoid, ['salonFlags', SF.tmpPaymentArrival]);

const PaymentArrival = ({ total, value, date, isBrightMode, isDarkMode, isLightMode, salonFlags }) => {
  const { t } = useTranslation();

  if (getFlagPaymentArrival(salonFlags) !== true) {
    return null;
  }

  const layoutClasses = cn({
    [CN.component]: true,
    [CN.dark]: isDarkMode,
    'dark-class': isDarkMode,
    'light-class': isLightMode,
    'bright-class': isBrightMode,
  });

  // SEE: https://getslick.atlassian.net/browse/GS-11514?focusedCommentId=15745
  const { transfers } = asObject(total);
  const payments = asArray(value);

  //TODO: date localisation
  const isWednesday = WEEKDAY.wed === nextTransferWeekday({ date });
  const nextPaymentFormatted = formatDayToYearWeekday(nextTransferTimestamp({ date }));
  const nextRefundFormatted = formatDayToYearWeekday(nextTransferTimestamp({ date, type: 'refund' }));

  const { refund, other } = splitPaymentsByOrigin({ payments });
  const totalRefund = sumArray(allFieldsAmount(refund).map(Math.abs)); // refunds come in negative

  if (other.length) {
    L.warn('()', 'Some payments were unaccounted for:', other);
  }

  return (
    <div data-bem="PaymentArrival" className={layoutClasses}>
      <p className={CN.description}>
        <Space>
          <strong className={CN.currency}>
            <CurrencySign value={transfers} />
          </strong>
        </Space>
        <Space>{t('cashUp.paymentArrival.description1Part1')}</Space>
        <Space>{t('cashUp.paymentArrival.description1Part2')}</Space>
        <Space>{t('cashUp.paymentArrival.description1Part3')}</Space>
        <Space>
          <strong className={CN.date}>{nextPaymentFormatted}</strong>
        </Space>
      </p>

      <p className={CN.description}>
        <Space>
          <strong className={CN.currency}>
            <CurrencySign value={totalRefund} />
          </strong>
        </Space>
        <Space>{t('cashUp.paymentArrival.description2Part1')}</Space>
        <Space>{t('cashUp.paymentArrival.description2Part2')}</Space>
        <Space>{t('cashUp.paymentArrival.description1Part3')}</Space>
        <Space>
          <strong className={CN.date}>{nextRefundFormatted}</strong>
        </Space>
      </p>

      {isWednesday && (
        <p className={CN.description}>
          <Space>
            <strong className={CN.note}>{t('cashUp.paymentArrival.description3Part1')}</strong>{' '}
            {t('cashUp.paymentArrival.description3Part2')}
          </Space>
          <Space>{t('cashUp.paymentArrival.description3Part3')}</Space>
        </p>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonFlags: selectCurrentSalonFlags(state),
});

const PaymentArrivalHoc = connect(mapStateToProps)(withTheme({ hasBright: true })(PaymentArrival));

export default PaymentArrivalHoc;
